/**
 * Core's own card types: a statistic, and the recent activity. Declared like
 * a module's, so the dashboard treats all of them the same way.
 *
 * The page deliberately shows **no aggregate the API does not already
 * answer**. There is no "total files" statistic, because there is no endpoint
 * for it and counting them here would mean walking the tree.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SvgIconComponent } from '@mui/icons-material';
import { listAuditEvents } from '../../api/audit';
import { listGroups, listRoles, listUsers } from '../../api/identity';
import { listDeliveries } from '../../api/notifications';
import { listLibraries } from '../../api/resources';
import { useAuth } from '../../auth/useAuth';
import { Panel } from '../../components/Panel';
import { StatTile } from '../../components/StatTile';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { defineDashboardCard, type DashboardCardType, type DashboardContext } from '../../modules/types';

// --- a statistic -------------------------------------------------------------

/** Every statistic reads `page.total` off a one-row page, rather than counting rows. */
const COUNT_ONLY = { limit: 1 } as const;

interface StatDefinition {
  readonly icon: SvgIconComponent;
  readonly labelKey: `dashboard.stat.${'libraries' | 'users' | 'groups' | 'roles' | 'pendingDeliveries'}`;
  readonly hintKey?: 'dashboard.stat.pendingHint';
  readonly to: string;
  /** Null: a session is enough. */
  readonly permission: string | null;
  readonly count: () => Promise<number | undefined>;
}

const STATS = {
  libraries: {
    icon: LayersIcon,
    labelKey: 'dashboard.stat.libraries',
    to: '/files',
    permission: null,
    count: async () => (await listLibraries()).libraries.length,
  },
  users: {
    icon: PeopleIcon,
    labelKey: 'dashboard.stat.users',
    to: '/users',
    permission: 'user.read',
    count: async () => (await listUsers(COUNT_ONLY)).page.total ?? undefined,
  },
  groups: {
    icon: GroupsIcon,
    labelKey: 'dashboard.stat.groups',
    to: '/groups',
    permission: 'group.read',
    count: async () => (await listGroups(COUNT_ONLY)).page.total ?? undefined,
  },
  roles: {
    icon: BadgeIcon,
    labelKey: 'dashboard.stat.roles',
    to: '/roles',
    permission: 'role.read',
    count: async () => (await listRoles(COUNT_ONLY)).page.total ?? undefined,
  },
  pendingDeliveries: {
    icon: SendIcon,
    labelKey: 'dashboard.stat.pendingDeliveries',
    hintKey: 'dashboard.stat.pendingHint',
    to: '/notifications/deliveries',
    permission: 'notification.read',
    count: async () => (await listDeliveries({ status: 'pending', ...COUNT_ONLY })).page.total ?? undefined,
  },
} as const satisfies Record<string, StatDefinition>;

type StatKey = keyof typeof STATS;
const STAT_KEYS = Object.keys(STATS) as StatKey[];

function isStat(value: unknown): value is StatKey {
  return typeof value === 'string' && Object.hasOwn(STATS, value);
}

/** The statistics this caller may read: a card's choices, and the defaults. */
function readableStats(ctx: DashboardContext): StatKey[] {
  return STAT_KEYS.filter((key) => {
    const permission: string | null = STATS[key].permission;
    return permission === null || ctx.can(permission);
  });
}

type StatConfig = { readonly stat: string };

function StatCard({ config }: { readonly config: StatConfig }) {
  const { t } = useTranslation('system');
  const { can } = useAuth();
  const known = isStat(config.stat);
  const definition: StatDefinition | undefined = known ? STATS[config.stat as StatKey] : undefined;
  const allowed = definition !== undefined && (definition.permission === null || can(definition.permission));

  const value = useAsync(() => definition!.count(), [config.stat], { enabled: allowed });
  if (!definition || !allowed) return null;

  return (
    <StatTile
      icon={definition.icon}
      label={t(definition.labelKey)}
      value={value.data}
      to={definition.to}
      {...(definition.hintKey ? { hint: t(definition.hintKey) } : {})}
    />
  );
}

const statCard = defineDashboardCard<StatConfig>({
  id: 'stat',
  titleKey: 'system:dashboard.card.stat',
  icon: InsertChartOutlinedIcon,
  component: StatCard,
  width: 1,
  minWidth: 1,
  maxWidth: 1,
  order: 100,
  defaultConfig: { stat: 'libraries' },
  settings: [
    {
      kind: 'select',
      key: 'stat',
      labelKey: 'system:dashboard.field.stat',
      options: (ctx) =>
        readableStats(ctx).map((key) => ({ value: key, labelKey: `system:${STATS[key].labelKey}` as const })),
    },
  ],
  // One card for each statistic the caller can read.
  defaults: (ctx) => readableStats(ctx).map((key) => ({ key, config: { stat: key } })),
});

// --- the recent activity -----------------------------------------------------

const RECENT_EVENTS = 8;

function RecentActivityCard() {
  const { t } = useTranslation('system');
  const recent = useAsync(() => listAuditEvents({ limit: RECENT_EVENTS }), []);

  return (
    <Panel
      icon={<HistoryIcon fontSize="small" />}
      title={t('dashboard.recentActivity')}
      actions={
        <Typography
          component={RouterLink}
          to="/audit"
          variant="body2"
          sx={{ color: 'primary.main', textDecoration: 'none' }}
        >
          {t('dashboard.wholeLog')}
        </Typography>
      }
    >
      <Stack spacing={1}>
        {recent.data === undefined
          ? Array.from({ length: 4 }, (_, index) => <Skeleton key={index} variant="rounded" height={32} />)
          : recent.data.events.map((event) => (
              <Stack
                key={event.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.25, sm: 1.5 }}
                sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={event.event}
                  sx={{ fontFamily: 'monospace', fontSize: 11 }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {event.actor.label}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' · '}
                    {event.entityType}:{event.entityId}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Timestamp value={event.occurredAt} />
                </Typography>
              </Stack>
            ))}
      </Stack>
    </Panel>
  );
}

const recentActivityCard = defineDashboardCard<Record<string, never>>({
  id: 'recentActivity',
  titleKey: 'system:dashboard.card.recentActivity',
  icon: HistoryIcon,
  anyOf: ['audit.read'],
  component: RecentActivityCard,
  width: 3,
  minWidth: 2,
  maxWidth: 3,
  order: 900,
  defaultConfig: {},
  defaults: () => [{ key: 'main', config: {} }],
});

export const CORE_DASHBOARD_CARDS: readonly DashboardCardType[] = [statCard, recentActivityCard];
